import React, { useState } from "react";
import { CustomTabPanel } from "../../../components/tabComponent";
import { icons } from "../../../constants";
import { riskStatus, controlType, controlFrequency } from "../../../constants/selectData";
import LaddaButton from "../../../components/buttons/laddaButton";
import RiskStatusModal from "./riskStatusModal";
import { StyledTooltip } from "../../../components/toolltipComponent";
import RiskVerificationModal from "../riskDetails/riskVerificationModal";
import { riskHelpIds } from "../../createRisk/riskHelpData";
import ModalComponentRiskHelp from "../../createRisk/modalRiskHelp";
import { isRiskVersion } from "../../../utils/riskAndOportunityVersion";

const RiskStatusTab = ({
  data,
  getRisk,
  isEditionAvailable,
  updateRiskupdateDate
}) => {
  const [riskStatusModal, setRiskStatusModal] = useState(false);
  const [riskVerificationModal, setRiskVerificationModal] = useState(false);
  const [helpId, setHelpId] = useState(0);
  const [openHelpModal, setOpenHelpModal] = useState(false);

  const handleClose = () => {
    setRiskStatusModal(false);
    setRiskVerificationModal(false);
  };

  const openModalVerification = () => {
    setRiskVerificationModal(true);
  };

  const getResidualLevel = (residualRiskLevel) => {
    switch (residualRiskLevel) {
      case 0:
      case 1:
        return {
          class: "low-level",
          textClass: "low-level-text",
          text: "Tolerable"
        };
      case 2:
        return {
          class: "medium-level",
          textClass: "medium-level-text",
          text: "Moderado"
        };
      case 3:
        return {
          class: "high-level",
          textClass: "high-level-text",
          text: "Importante"
        };
      case 4:
        return {
          class: "extreme-level",
          textClass: "extreme-level-text",
          text: "Inaceptable"
        };
      default:
        return {
          class: "default-level",
          textClass: "default-level-text",
          text: "Sin Nivel"
        };
    }
  };

  const getRiskLevel = (levelItem, hasVulnerability) => {
    const level = parseInt(levelItem, 10);
    if (hasVulnerability && level === 4) {
      return {
        class: "superior-level",
        textClass: "superior-level-text",
        text: "Nivel Superior"
      };
    }
    if (level === 4 && !hasVulnerability) {
      return {
        class: "high-level",
        textClass: "high-level-text",
        text: "Nivel Alto"
      };
    }
    switch (level) {
      case 1:
        return {
          class: "low-level",
          textClass: "low-level-text",
          text: "Nivel Bajo"
        };
      case 2:
        return {
          class: "medium-level",
          textClass: "medium-level-text",
          text: "Medio"
        };
      case 3:
        return {
          class: "high-level",
          textClass: "high-level-text",
          text: "Nivel Alto"
        };
      default:
        return {
          class: "default-level",
          textClass: "default-level-text",
          text: "Sin Nivel"
        };
    }
  };

  const getFrecuency = (residualRiskLevel) => {
    switch (residualRiskLevel) {
      case 1:
        return "Ocasional";
      case 2:
        return "Periódico";
      case 3:
        return "Permanente";
      default:
        return "";
    }
  };

  const getProbability = (value) => {
    const map = { 1: "Raro", 2: "Improbable", 3: "Posible", 4: "Probable", 5: "Casi seguro" };
    return map[parseInt(value, 10)] || "";
  };

  const getImpact = (value) => {
    const map = { 1: "Insignificante", 2: "Menor", 3: "Moderado", 4: "Mayor", 5: "Enorme" };
    return map[parseInt(value, 10)] || "";
  };

  const getVulnerability = (value) => {
    const map = { 1: "Baja", 3: "Media", 5: "Alta" };
    return map[parseInt(value, 10)] || "";
  };

  const getControlTypeLabel = (value) =>
    controlType.find((c) => parseInt(c.value, 10) === parseInt(value, 10))?.name ?? "";

  const getFrequencyLabel = (value) =>
    controlFrequency.find((c) => parseInt(c.value, 10) === parseInt(value, 10))?.name ?? "";

  const useVersion2 = isRiskVersion(data?.year, data?.month) === 2;

  const RiskLevelComponent = ({ levelItem, hasVulnerability: hasVuln }) => {
    const useV2 = hasVuln ?? useVersion2;
    const { class: levelClass, textClass, text } = getRiskLevel(levelItem, useV2);
    // Solo en v2 se muestra Vulnerabilidad en el tooltip (v1 no la usa).
    const showVulnerabilityInTooltip = useVersion2;

    return (
      <StyledTooltip
        title={
          <>
            <div className="HContainer">
              <p className="toolkit-title">Probabilidad:</p>
              <p className="toolkit-content">{getProbability(data?.probability)}</p>
            </div>
            <div className="HContainer">
              <p className="toolkit-title">Impacto:</p>
              <p className="toolkit-content">{getImpact(data?.impact)}</p>
            </div>
            {showVulnerabilityInTooltip && (
              <div className="HContainer">
                <p className="toolkit-title">Vulnerabilidad:</p>
                <p className="toolkit-content">{getVulnerability(data?.vulnerability) || "—"}</p>
              </div>
            )}
          </>
        }
        followCursor
      >
        <div className={`level ${levelClass}`}>
          <p className={textClass}>{text}</p>
        </div>
      </StyledTooltip>
    );
  };

  const controlEffectivenessLevel = (levelItem) => {
    switch (levelItem) {
      case 1:
        return {
          class: "extreme-level",
          textClass: "extreme-level-text",
          text: "Inexsistente"
        };
      case 2:
      case 3:
      case 4:
        return {
          class: "low-level",
          textClass: "low-level-text",
          text: "Bajo"
        };
      case 5:
      case 6:
      case 7:
      case 8:
        return {
          class: "medium-level",
          textClass: "medium-level-text",
          text: "Medio"
        };
      case 9:
      case 10:
      case 11:
      case 12:
        return {
          class: "high-level",
          textClass: "high-level-text",
          text: "Alto"
        };
      default:
        return {
          class: "default-level",
          textClass: "default-level-text",
          text: "Sin Nivel"
        };
    }
  };

  const handleCloseHelp = () => {
    setOpenHelpModal(false);
  };

  const ControlEffectivenessComponent = ({ levelItem }) => {
    const {
      class: levelClass,
      textClass,
      text
    } = controlEffectivenessLevel(levelItem);

    const tooltipTitle = (
      <>
        <div className="HContainer">
          <p className="toolkit-title">Tipo de Control:</p>
          <p className="toolkit-content">{getControlTypeLabel(data?.typeControl)}</p>
        </div>
        <div className="HContainer">
          <p className="toolkit-title">Frecuencia del Control:</p>
          <p className="toolkit-content">{getFrequencyLabel(data?.frecuencyControl)}</p>
        </div>
      </>
    );

    return (
      <StyledTooltip title={tooltipTitle} followCursor>
        <div className={`level ${levelClass}`}>
          <p className={textClass}>{text}</p>
        </div>
      </StyledTooltip>
    );
  };

  const ResidualLevelComponent = ({ levelItem }) => {
    const { class: levelClass, textClass, text } = getResidualLevel(levelItem);
    const riskLevelInfo = getRiskLevel(data?.riskLevel, useVersion2);
    const effInfo = controlEffectivenessLevel(data?.eficiencyControl);

    const tooltipTitle = (
      <>
        <div className="HContainer">
          <p className="toolkit-title">Nivel de Riesgo:</p>
          <p className="toolkit-content">{riskLevelInfo?.text ?? ""}</p>
        </div>
        <div className="HContainer">
          <p className="toolkit-title">Eficacia de Control:</p>
          <p className="toolkit-content">{effInfo?.text ?? ""}</p>
        </div>
      </>
    );

    return (
      <StyledTooltip title={tooltipTitle} followCursor>
        <div className={`level ${levelClass}`}>
          <p className={textClass}>{text}</p>
        </div>
      </StyledTooltip>
    );
  };

  const riskStatusDetailModal = () => {
    setRiskStatusModal(true);
  };

  return (
    <div>
      <CustomTabPanel value="2">
        <div className="IHContainer align-center">
          <h2 className="gm-title">Estado del Riesgo:</h2>
          <p style={{ marginLeft: "20px" }}>
            {data?.state
              ? riskStatus.find((item) => item.value === data.state)?.name
              : "" || ""}
          </p>
        </div>

        <h2 className="gm-title">Estatus del Riesgo:</h2>
        <p className="content-text">{data?.statusRisk}</p>

        <div className="IHContainer align-center">
          <div className="IHContainer align-center">
            <h2 className="gm-title">Riesgo Residual:</h2>
            <div
              className="pointer align-center j-center ml-5"
              onClick={() => {
                setHelpId(riskHelpIds.RESIDUALRISK);
                setOpenHelpModal(true);
              }}
            >
              <img
                className="btn-add-icon"
                src={icons.infoExclamation}
                alt="icon"
                style={{ margin: "0 0 7px 0", width: "30px" }}
              />
            </div>
          </div>
          <ResidualLevelComponent levelItem={data?.residualRisk} />
        </div>

        <div className="IHContainer align-center">
          <div className="IHContainer align-center">
            <p className="gm-title">Nivel de Riesgo:</p>
            <div
              className="pointer align-center j-center ml-5"
              onClick={() => {
                setHelpId(riskHelpIds.RISKLEVEL);
                setOpenHelpModal(true);
              }}
            >
              <img
                className="btn-add-icon"
                src={icons.infoExclamation}
                alt="icon"
                style={{ margin: "0 0 7px 0", width: "30px" }}
              />
            </div>
          </div>
          <RiskLevelComponent levelItem={data.riskLevel} hasVulnerability={useVersion2} />
        </div>
        <div className="IHContainer align-center">
          <div className="IHContainer align-center">
            <p className="gm-title">Eficacia de Control:</p>
            <div
              className="pointer align-center j-center ml-5"
              onClick={() => {
                setHelpId(riskHelpIds.CONTROLEFFECTIVENESS);
                setOpenHelpModal(true);
              }}
            >
              <img
                className="btn-add-icon"
                src={icons.infoExclamation}
                alt="icon"
                style={{ margin: "0 0 7px 0", width: "30px" }}
              />
            </div>
          </div>
          <ControlEffectivenessComponent levelItem={data.eficiencyControl} />
        </div>

        <h2 className="gm-title">Evidencia del Riesgo Residual:</h2>
        <p className="content-text">{data?.evidenceResidualRisk}</p>

        <div className="IHContainer">
          <h2 className="gm-title">Eficaz:</h2>
          <p>{data?.effective == 1 ? "Sí" : data.effective == 0 ? "No" : ""}</p>
        </div>

        <h2 className="gm-title">Verificación:</h2>
        <p className="content-text">{data?.verification}</p>

        <div className="j-center">
          {isEditionAvailable && (
            <LaddaButton variant="contained" onClick={riskStatusDetailModal}>
              <div className="IHContainer align-right">
                <img
                  className="btn-icon"
                  src={icons.editWhite}
                  alt="icon"
                  style={{ marginRight: "10px" }}
                />
                <p className="button_text">Editar</p>
              </div>
            </LaddaButton>
          )}

          {isEditionAvailable && (
            <LaddaButton
              variant="contained"
              onClick={() => {
                openModalVerification();
              }}
            >
              <div className="IHContainer align-rigth">
                <img
                  className="btn-icon"
                  src={icons.editWhite}
                  alt="icon"
                  style={{ marginRight: "10px" }}
                />
                <p className="button_text">Verificación</p>
              </div>
            </LaddaButton>
          )}
        </div>
      </CustomTabPanel>

      <RiskStatusModal
        open={riskStatusModal}
        handleClose={handleClose}
        data={data}
        getRisk={getRisk}
        isEditionAvailable={isEditionAvailable}
        updateRiskupdateDate={updateRiskupdateDate}
      />

      <RiskVerificationModal
        open={riskVerificationModal}
        handleClose={handleClose}
        data={data}
        getRisk={getRisk}
      />
      <ModalComponentRiskHelp
        openModal={openHelpModal}
        handleClose={handleCloseHelp}
        helpId={helpId}
      />
    </div>
  );
};

export default RiskStatusTab;
